import mediaService from '@/services/mediaService';
import templatesService from '@/services/templatesService';
import { toastService } from '@/services/toastService';
import {
  AlignJustify,
  Bold,
  Clock,
  FileText,
  GitBranch,
  Image as ImageIcon,
  Italic,
  Layout,
  List,
  Loader2,
  MessageSquare,
  Minus,
  Plus,
  Globe,
  Save,
  Strikethrough,
  Trash2,
  Type,
  Upload,
  Video,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const extractVariablesCount = text => {
  if (!text) return 0;
  const matches = text.match(/\{\{(\d+)\}\}/g);
  if (!matches) return 0;
  let max = 0;
  matches.forEach(m => {
    const num = parseInt(m.replace(/[\{\}]/g, ''), 10);
    if (num > max) max = num;
  });
  return max;
};

const NodeConfigSidebar = ({
  selectedNode,
  onUpdate,
  onSave,
  onClose,
  onDelete,
  whatsappConfig,
}) => {
  const [data, setData] = useState(selectedNode?.data || {});
  const [keywordsText, setKeywordsText] = useState(
    selectedNode?.data?.keywords?.join(', ') || ''
  );
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [payloadsText, setPayloadsText] = useState(
    selectedNode?.data?.payloads?.join(', ') || ''
  );
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const docFileInputRef = useRef(null);
  const imageFileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);

  // Auto-sync local state to node data on canvas for realtime preview
  useEffect(() => {
    // Only update if it's a real change and node is selected
    if (
      selectedNode &&
      JSON.stringify(data) !== JSON.stringify(selectedNode.data)
    ) {
      const timeoutId = setTimeout(() => {
        onUpdate(selectedNode.id, data);
      }, 100); // Small debounce to prevent too many updates
      return () => clearTimeout(timeoutId);
    }
  }, [data, selectedNode, onUpdate]);

  useEffect(() => {
    setData(selectedNode?.data || {});
    setKeywordsText(selectedNode?.data?.keywords?.join(', ') || '');
    setPayloadsText(selectedNode?.data?.payloads?.join(', ') || '');
    setUploadedFileName(null);
  }, [selectedNode?.id]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (
        (selectedNode?.type === 'triggerNode' ||
          selectedNode?.type === 'messageNode') &&
        whatsappConfig?.projectId
      ) {
        try {
          setLoadingTemplates(true);
          const response = await templatesService.getTemplates(
            whatsappConfig.projectId,
            { status: 'APPROVED' }
          );
          if (response.success) {
            setTemplates(response.templates || response.docs || []);
          }
        } catch (error) {
          console.error('Error fetching templates:', error);
        } finally {
          setLoadingTemplates(false);
        }
      }
    };
    fetchTemplates();
  }, [selectedNode, whatsappConfig]);

  if (!selectedNode) return null;

  const handleChange = e => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleKeywordsChange = e => {
    const val = e.target.value;
    setKeywordsText(val);
    const keywords = val
      .split(',')
      .map(s => s.trim())
      .filter(s => s !== '');
    setData(prev => ({ ...prev, keywords }));
  };

  const handlePayloadsChange = e => {
    const val = e.target.value;
    setPayloadsText(val);
    const payloads = val
      .split(',')
      .map(s => s.trim())
      .filter(s => s !== '');
    setData(prev => ({ ...prev, payloads }));
  };

  const handleTemplateSelect = templateId => {
    const template = templates.find(t => t._id === templateId);
    if (template) {
      // Direct fields from our Template model
      const header = template.header;
      const body = template.body;
      const footer = template.footer;
      const buttons = template.buttons || [];

      setData(prev => ({
        ...prev,
        templateId: template._id,
        templateName: template.name,
        template: {
          header,
          body,
          footer,
          buttons,
        },
      }));

      // Auto-populate payloads with quick_reply button texts if available
      const quickReplies = buttons
        .filter(b => b.type === 'quick_reply')
        .map(b => b.text);

      if (quickReplies.length > 0) {
        const currentPayloads = quickReplies;
        setPayloadsText(currentPayloads.join(', '));
        setData(prev => ({ ...prev, payloads: currentPayloads }));
      }
    } else {
      setData(prev => ({
        ...prev,
        templateId: '',
        templateName: '',
        template: null,
      }));
    }
  };

  const insertFormatting = (prefix, suffix = prefix) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = data.message || '';
    const selectedText = text.substring(start, end);
    const before = text.substring(0, start);
    const after = text.substring(end);

    const newMessage = `${before}${prefix}${selectedText}${suffix}${after}`;
    setData(prev => ({ ...prev, message: newMessage }));

    // Refocus and set selection after state update (using timeout for React cycle)
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  };

  const handleAddButton = () => {
    const buttons = data.buttons || [];
    if (buttons.length >= 3) return;
    setData(prev => ({
      ...prev,
      buttons: [...buttons, { id: Date.now().toString(), label: 'New Button' }],
    }));
  };

  const handleRemoveButton = buttonId => {
    setData(prev => ({
      ...prev,
      buttons: (prev.buttons || []).filter(b => b.id !== buttonId),
    }));
  };

  const handleButtonLabelChange = (buttonId, newLabel) => {
    setData(prev => ({
      ...prev,
      buttons: (prev.buttons || []).map(b =>
        b.id === buttonId ? { ...b, label: newLabel } : b
      ),
    }));
  };

  const handleAddListItem = () => {
    const items = data.listItems || [];
    if (items.length >= 10) return;
    setData(prev => ({
      ...prev,
      listItems: [
        ...items,
        { id: Date.now().toString(), title: 'Option', description: '' },
      ],
    }));
  };

  const handleRemoveListItem = itemId => {
    setData(prev => ({
      ...prev,
      listItems: (prev.listItems || []).filter(i => i.id !== itemId),
    }));
  };

  const handleListItemChange = (itemId, field, value) => {
    setData(prev => ({
      ...prev,
      listItems: (prev.listItems || []).map(i =>
        i.id === itemId ? { ...i, [field]: value } : i
      ),
    }));
  };

  // Upload handler for standalone image / video / document message types
  const handleDirectMediaUpload = async (e, mediaType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!whatsappConfig?.phoneNumberId) {
      toastService.showError('WhatsApp Configuration not found for this project');
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (mediaType === 'image' && fileSizeMB > 5) {
      toastService.showError('Image must be less than 5MB');
      return;
    }
    if (mediaType === 'video' && fileSizeMB > 16) {
      toastService.showError('Video must be less than 16MB');
      return;
    }
    if (mediaType === 'document' && fileSizeMB > 100) {
      toastService.showError('Document must be less than 100MB');
      return;
    }

    try {
      setUploading(true);
      const response = await mediaService.uploadWhatsAppMedia(
        whatsappConfig.projectId,
        whatsappConfig.phoneNumberId,
        file
      );

      if (response.success) {
        setUploadedFileName(file.name);
        setData(prev => ({
          ...prev,
          mediaId: response.data.id,
          mediaKey: response.data.key || file.name,
          mediaFileName: file.name,
        }));
        toastService.showSuccess('File uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toastService.showError('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleMediaUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;

    if (!whatsappConfig?.phoneNumberId) {
      toastService.showError(
        'WhatsApp Configuration not found for this project'
      );
      return;
    }

    // Validation
    const format =
      data.type === 'template'
        ? data.template?.header?.format
        : data.headerType;

    const fileSizeMB = file.size / (1024 * 1024);
    if (['image', 'IMAGE'].includes(format) && fileSizeMB > 5) {
      toastService.showError('Image must be less than 5MB');
      return;
    }
    if (['video', 'VIDEO'].includes(format) && fileSizeMB > 16) {
      toastService.showError('Video must be less than 16MB');
      return;
    }
    if (['document', 'DOCUMENT'].includes(format) && fileSizeMB > 100) {
      toastService.showError('Document must be less than 100MB');
      return;
    }

    try {
      setUploading(true);
      const response = await mediaService.uploadWhatsAppMedia(
        whatsappConfig.projectId,
        whatsappConfig.phoneNumberId,
        file
      );

      if (response.success) {
        setData(prev => ({
          ...prev,
          headerMediaId: response.data.id,
          headerMediaKey: response.data.key || file.name,
        }));
        toastService.showSuccess('File uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toastService.showError('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    onSave(selectedNode.id, data);
  };

  const renderConfig = () => {
    switch (selectedNode.type) {
      case 'triggerNode':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                Trigger Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() =>
                    setData(prev => ({ ...prev, triggerType: 'keyword' }))
                  }
                  className={`px-3 py-2 text-xs font-bold rounded-xl border transition-all ${
                    (data.triggerType || 'keyword') === 'keyword'
                      ? 'bg-primary-50 border-primary-500 text-primary-700 shadow-sm'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  Keyword Match
                </button>
                <button
                  onClick={() =>
                    setData(prev => ({
                      ...prev,
                      triggerType: 'template_response',
                    }))
                  }
                  className={`px-3 py-2 text-xs font-bold rounded-xl border transition-all ${
                    data.triggerType === 'template_response'
                      ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  Template Response
                </button>
              </div>
            </div>

            {data.triggerType === 'template_response' ? (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                    Select Template
                  </label>
                  <select
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 transition-all"
                    value={data.templateId || ''}
                    onChange={e => handleTemplateSelect(e.target.value)}
                  >
                    <option value="">-- Choose an Approved Template --</option>
                    {templates.map(t => (
                      <option key={t._id} value={t._id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {loadingTemplates && (
                    <p className="text-[10px] text-gray-400 mt-1 italic">
                      Loading your templates...
                    </p>
                  )}
                </div>

                {data.templateName && (
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                    <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-widest mb-1">
                      Active Configuration
                    </p>
                    <p className="text-xs text-emerald-700">
                      This flow will trigger when a user interacts with any
                      button in <strong>{data.templateName}</strong>. Each
                      button has a separate output socket on the canvas.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                    Keywords (comma separated)
                  </label>
                  <textarea
                    className="w-full h-24 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm leading-relaxed"
                    value={keywordsText}
                    onChange={handleKeywordsChange}
                    placeholder="Hello, Hi, Start, Menu..."
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Flow starts when a message matches one of these keywords
                    exactly.
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      case 'messageNode':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                Message Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'text', icon: <Type size={16} />, label: 'Text' },
                  {
                    id: 'image',
                    icon: <ImageIcon size={16} />,
                    label: 'Image',
                  },
                  { id: 'video', icon: <Video size={16} />, label: 'Video' },
                  {
                    id: 'document',
                    icon: <FileText size={16} />,
                    label: 'Doc',
                  },
                  { id: 'buttons', icon: <List size={16} />, label: 'Buttons' },
                  { id: 'list', icon: <AlignJustify size={16} />, label: 'List' },
                  {
                    id: 'template',
                    icon: <Layout size={16} />,
                    label: 'Template',
                  },
                ].map(type => (
                  <button
                    key={type.id}
                    onClick={() =>
                      setData(prev => ({
                        // Keep only type-agnostic fields
                        message: '',
                        type: type.id,
                        // Preserve button/list state when switching between related types
                        buttons: type.id === 'buttons' ? (prev.buttons || []) : undefined,
                        listItems: type.id === 'list' ? (prev.listItems || []) : undefined,
                        listButtonText: type.id === 'list' ? (prev.listButtonText || '') : undefined,
                        listTitle: type.id === 'list' ? (prev.listTitle || '') : undefined,
                        footer: ['buttons', 'list'].includes(type.id) ? (prev.footer || '') : undefined,
                        headerType: type.id === 'buttons' ? (prev.headerType || 'none') : undefined,
                        headerText: type.id === 'buttons' ? (prev.headerText || '') : undefined,
                        headerMediaId: type.id === 'buttons' ? (prev.headerMediaId || '') : undefined,
                        headerMediaKey: type.id === 'buttons' ? (prev.headerMediaKey || '') : undefined,
                      }))
                    }
                    className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all ${
                      (data.type || 'text') === type.id
                        ? 'bg-blue-50 border-blue-500 text-blue-600 shadow-sm'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {type.icon}
                    <span className="text-[10px] font-bold mt-1 uppercase">
                      {type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* ── IMAGE ── */}
            {data.type === 'image' && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                    Image File
                  </label>
                  <input
                    type="file"
                    className="hidden"
                    ref={imageFileInputRef}
                    accept="image/*"
                    onChange={e => handleDirectMediaUpload(e, 'image')}
                  />
                  <button
                    onClick={() => imageFileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full flex flex-col items-center justify-center p-6 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-400 transition-all group"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-7 w-7 text-blue-500 animate-spin mb-2" />
                        <span className="text-xs font-bold text-gray-500">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <div className="p-3 bg-blue-100 rounded-full mb-3 group-hover:scale-110 transition-transform">
                          <ImageIcon className="h-6 w-6 text-blue-500" />
                        </div>
                        {data.mediaId ? (
                          <>
                            <span className="text-xs font-bold text-green-600">✓ Image uploaded</span>
                            <p className="text-[10px] text-gray-500 mt-1 truncate max-w-full px-4">{data.mediaFileName || data.mediaKey}</p>
                            <span className="text-[10px] text-blue-500 mt-1">Click to replace</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs font-bold text-gray-600">Click to upload image</span>
                            <span className="text-[10px] text-gray-400 mt-1">PNG, JPG, WEBP · Max 5MB</span>
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                    Caption (Optional)
                  </label>
                  <input
                    type="text"
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500"
                    name="message"
                    value={data.message || ''}
                    onChange={handleChange}
                    placeholder="Add a caption..."
                    maxLength={1024}
                  />
                </div>
              </div>
            )}

            {/* ── VIDEO ── */}
            {data.type === 'video' && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                    Video File
                  </label>
                  <input
                    type="file"
                    className="hidden"
                    ref={videoFileInputRef}
                    accept="video/*"
                    onChange={e => handleDirectMediaUpload(e, 'video')}
                  />
                  <button
                    onClick={() => videoFileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full flex flex-col items-center justify-center p-6 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl hover:bg-purple-50 hover:border-purple-400 transition-all group"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-7 w-7 text-purple-500 animate-spin mb-2" />
                        <span className="text-xs font-bold text-gray-500">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <div className="p-3 bg-purple-100 rounded-full mb-3 group-hover:scale-110 transition-transform">
                          <Video className="h-6 w-6 text-purple-500" />
                        </div>
                        {data.mediaId ? (
                          <>
                            <span className="text-xs font-bold text-green-600">✓ Video uploaded</span>
                            <p className="text-[10px] text-gray-500 mt-1 truncate max-w-full px-4">{data.mediaFileName || data.mediaKey}</p>
                            <span className="text-[10px] text-purple-500 mt-1">Click to replace</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs font-bold text-gray-600">Click to upload video</span>
                            <span className="text-[10px] text-gray-400 mt-1">MP4, 3GP · Max 16MB</span>
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                    Caption (Optional)
                  </label>
                  <input
                    type="text"
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500"
                    name="message"
                    value={data.message || ''}
                    onChange={handleChange}
                    placeholder="Add a caption..."
                    maxLength={1024}
                  />
                </div>
              </div>
            )}

            {/* ── DOCUMENT ── */}
            {data.type === 'document' && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widests">
                    Document File
                  </label>
                  <input
                    type="file"
                    className="hidden"
                    ref={docFileInputRef}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={e => handleDirectMediaUpload(e, 'document')}
                  />
                  <button
                    onClick={() => docFileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full flex flex-col items-center justify-center p-6 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl hover:bg-orange-50 hover:border-orange-400 transition-all group"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-7 w-7 text-orange-500 animate-spin mb-2" />
                        <span className="text-xs font-bold text-gray-500">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <div className="p-3 bg-orange-100 rounded-full mb-3 group-hover:scale-110 transition-transform">
                          <FileText className="h-6 w-6 text-orange-500" />
                        </div>
                        {data.mediaId ? (
                          <>
                            <span className="text-xs font-bold text-green-600">✓ Document uploaded</span>
                            <p className="text-[10px] text-gray-500 mt-1 truncate max-w-full px-4">{data.mediaFileName || data.mediaKey}</p>
                            <span className="text-[10px] text-orange-500 mt-1">Click to replace</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs font-bold text-gray-600">Click to upload document</span>
                            <span className="text-[10px] text-gray-400 mt-1">PDF, DOCX, XLSX, CSV, PPT · Max 100MB</span>
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                    Caption (Optional)
                  </label>
                  <input
                    type="text"
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                    name="message"
                    value={data.message || ''}
                    onChange={handleChange}
                    placeholder="Add a caption..."
                    maxLength={1024}
                  />
                </div>
              </div>
            )}

            {/* ── LIST ── */}
            {data.type === 'list' && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                      Message Body
                    </label>
                    <div className="flex gap-1">
                      <button onClick={() => insertFormatting('*')} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Bold"><Bold size={14} /></button>
                      <button onClick={() => insertFormatting('_')} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Italic"><Italic size={14} /></button>
                    </div>
                  </div>
                  <div className="relative">
                    <textarea
                      ref={textareaRef}
                      className="w-full h-24 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm leading-relaxed"
                      name="message"
                      value={data.message || ''}
                      onChange={handleChange}
                      placeholder="Type the list message body..."
                      maxLength={1024}
                    />
                    <div className="absolute bottom-2 right-3 text-[10px] font-bold text-gray-400">
                      {(data.message || '').length}/1024
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5 tracking-widest">Button Text</label>
                    <input
                      type="text"
                      className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500"
                      name="listButtonText"
                      value={data.listButtonText || ''}
                      onChange={handleChange}
                      placeholder="Select Option"
                      maxLength={20}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5 tracking-widest">Footer (Optional)</label>
                    <input
                      type="text"
                      className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500"
                      name="footer"
                      value={data.footer || ''}
                      onChange={handleChange}
                      placeholder="Optional footer"
                      maxLength={60}
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                      List Items ({data.listItems?.length || 0}/10)
                    </label>
                    <button
                      onClick={handleAddListItem}
                      disabled={(data.listItems?.length || 0) >= 10}
                      className="p-1.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:bg-gray-200 transition-colors shadow-sm"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(data.listItems || []).map((item, idx) => (
                      <div key={item.id} className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-gray-500 uppercase">Row {idx + 1}</span>
                          <button
                            onClick={() => handleRemoveListItem(item.id)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                          >
                            <Minus size={14} />
                          </button>
                        </div>
                        <input
                          type="text"
                          className="w-full p-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          placeholder="Title (required)"
                          value={item.title}
                          onChange={e => handleListItemChange(item.id, 'title', e.target.value)}
                          maxLength={24}
                        />
                        <input
                          type="text"
                          className="w-full p-2 bg-white border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 text-gray-500"
                          placeholder="Description (optional)"
                          value={item.description || ''}
                          onChange={e => handleListItemChange(item.id, 'description', e.target.value)}
                          maxLength={72}
                        />
                      </div>
                    ))}
                    {(data.listItems || []).length === 0 && (
                      <div className="text-xs text-center py-4 text-gray-400 italic bg-gray-50 rounded-xl border border-dashed border-gray-200">
                        No list items yet. Click + to add rows.
                      </div>
                    )}
                  </div>
                </div>

                {/* Save reply as variable */}
                <div className="pt-3 border-t border-gray-100">
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5 tracking-widest">
                    Save Selected Option As
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500 font-mono text-sm font-bold">{'{{'}  </span>
                    <input
                      type="text"
                      className="w-full pl-9 pr-9 p-2.5 bg-teal-50 border border-teal-200 rounded-xl text-sm font-mono focus:ring-2 focus:ring-teal-500 text-teal-800 placeholder-teal-300"
                      name="responseVariable"
                      value={data.responseVariable || ''}
                      onChange={handleChange}
                      placeholder="userChoice"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-500 font-mono text-sm font-bold">{'}}'}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1.5 leading-relaxed">
                    In downstream nodes use <code className="bg-gray-100 px-1 rounded">{'{{userChoice}}'}</code> for the row ID and <code className="bg-gray-100 px-1 rounded">{'{{userChoice_title}}'}</code> for the label. <code className="bg-gray-100 px-1 rounded">{'{{_lastReply}}'}</code> always holds the last reply.
                  </p>
                </div>
              </div>
            )}

            {(data.type === 'text' ||
              data.type === 'buttons' ||
              data.type === 'template' ||
              !data.type) && (
              <div className="space-y-6">
                {(data.type === 'text' ||
                  data.type === 'buttons' ||
                  !data.type) && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Message Body
                      </label>
                      <div className="flex gap-1">
                        <button
                          onClick={() => insertFormatting('*')}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                          title="Bold"
                        >
                          <Bold size={14} />
                        </button>
                        <button
                          onClick={() => insertFormatting('_')}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                          title="Italic"
                        >
                          <Italic size={14} />
                        </button>
                        <button
                          onClick={() => insertFormatting('~')}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                          title="Strikethrough"
                        >
                          <Strikethrough size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="relative">
                      <textarea
                        ref={textareaRef}
                        className="w-full h-32 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm leading-relaxed"
                        name="message"
                        value={data.message || ''}
                        onChange={handleChange}
                        placeholder="Type your reply here..."
                        maxLength={1024}
                      />
                      <div className="absolute bottom-2 right-3 text-[10px] font-bold text-gray-400">
                        {(data.message || '').length}/1024
                      </div>
                    </div>
                  </div>
                )}

                {data.type === 'template' && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200 mt-4 pt-4 border-t border-gray-100">
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                        Choose Template
                      </label>
                      <select
                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                        value={data.templateId || ''}
                        onChange={e => handleTemplateSelect(e.target.value)}
                      >
                        <option value="">
                          -- Choose an Approved Template --
                        </option>
                        {templates.map(t => (
                          <option key={t._id} value={t._id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                      {loadingTemplates && (
                        <p className="text-[10px] text-gray-400 mt-1 italic leading-none">
                          Loading templates...
                        </p>
                      )}
                      {data.templateName && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                          <p className="text-[10px] font-bold text-blue-800 uppercase tracking-widest mb-1">
                            Interactive Template
                          </p>
                          <p className="text-xs text-blue-700">
                            Each button in <strong>{data.templateName}</strong>{' '}
                            will have a separate output handle on the node.
                          </p>
                        </div>
                      )}

                      {(() => {
                        const bodyVarsCount = data.template
                          ? extractVariablesCount(data.template.body?.text)
                          : 0;
                        const headerVarsCount =
                          data.template &&
                          data.template.header?.format === 'TEXT'
                            ? extractVariablesCount(data.template.header?.text)
                            : 0;
                        const hasVariables =
                          bodyVarsCount > 0 || headerVarsCount > 0;

                        if (!hasVariables) return null;

                        return (
                          <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                              Dynamic Variables
                            </p>
                            {headerVarsCount > 0 &&
                              Array.from({ length: headerVarsCount }).map(
                                (_, i) => (
                                  <div key={`header_var_${i}`}>
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                                      Header Variable {'{{'}
                                      {i + 1}
                                      {'}}'}
                                    </label>
                                    <input
                                      type="text"
                                      className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                      value={
                                        data.templateVariables?.header?.[i] ||
                                        ''
                                      }
                                      placeholder={`e.g. {{myApiData.data.name}}`}
                                      onChange={e => {
                                        const val = e.target.value;
                                        setData(prev => {
                                          const newVars = prev.templateVariables
                                            ? { ...prev.templateVariables }
                                            : { body: [], header: [] };
                                          const newHeader = [
                                            ...(newVars.header || []),
                                          ];
                                          newHeader[i] = val;
                                          return {
                                            ...prev,
                                            templateVariables: {
                                              ...newVars,
                                              header: newHeader,
                                            },
                                          };
                                        });
                                      }}
                                    />
                                  </div>
                                )
                              )}
                            {bodyVarsCount > 0 &&
                              Array.from({ length: bodyVarsCount }).map(
                                (_, i) => (
                                  <div key={`body_var_${i}`}>
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                                      Body Variable {'{{'}
                                      {i + 1}
                                      {'}}'}
                                    </label>
                                    <input
                                      type="text"
                                      className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                      value={
                                        data.templateVariables?.body?.[i] || ''
                                      }
                                      placeholder={`e.g. {{apiData.data.orderId}}`}
                                      onChange={e => {
                                        const val = e.target.value;
                                        setData(prev => {
                                          const newVars = prev.templateVariables
                                            ? { ...prev.templateVariables }
                                            : { body: [], header: [] };
                                          const newBody = [
                                            ...(newVars.body || []),
                                          ];
                                          newBody[i] = val;
                                          return {
                                            ...prev,
                                            templateVariables: {
                                              ...newVars,
                                              body: newBody,
                                            },
                                          };
                                        });
                                      }}
                                    />
                                  </div>
                                )
                              )}
                          </div>
                        );
                      })()}

                      {data.template?.header &&
                        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(
                          data.template.header.format
                        ) && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                              Header Media ({data.template.header.format})
                            </label>
                            <input
                              type="file"
                              className="hidden"
                              ref={fileInputRef}
                              onChange={handleMediaUpload}
                              accept={
                                data.template.header.format === 'IMAGE'
                                  ? 'image/*'
                                  : data.template.header.format === 'VIDEO'
                                    ? 'video/*'
                                    : '*'
                              }
                            />
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                              className="w-full flex flex-col items-center justify-center p-4 bg-white border-2 border-dashed border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-400 transition-all group"
                            >
                              {uploading ? (
                                <>
                                  <Loader2 className="h-6 w-6 text-blue-500 animate-spin mb-2" />
                                  <span className="text-xs font-bold text-gray-500">
                                    Uploading...
                                  </span>
                                </>
                              ) : (
                                <>
                                  <div className="p-2 bg-blue-50 rounded-full shadow-sm text-blue-400 group-hover:text-blue-500 transition-colors mb-2">
                                    <Upload size={20} />
                                  </div>
                                  <span className="text-xs font-bold text-gray-500">
                                    {data.headerMediaId
                                      ? 'Change Template Media'
                                      : 'Select Media for Template'}
                                  </span>
                                  {data.headerMediaId && (
                                    <p className="text-[10px] text-blue-600 mt-1 font-medium">
                                      ID: {data.headerMediaId}
                                    </p>
                                  )}
                                </>
                              )}
                            </button>
                            <p className="text-[10px] text-gray-400 mt-2">
                              {data.template.header.format === 'DOCUMENT' &&
                                'Max 100MB'}
                              {data.template.header.format === 'VIDEO' &&
                                'Max 16MB'}
                              {data.template.header.format === 'IMAGE' &&
                                'Max 5MB'}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {data.type === 'buttons' && (
                  <>
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                          Header Type
                        </label>
                        <select
                          className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500"
                          value={data.headerType || 'none'}
                          onChange={e =>
                            setData(prev => ({
                              ...prev,
                              headerType: e.target.value,
                            }))
                          }
                        >
                          <option value="none">None</option>
                          <option value="text">Text</option>
                          <option value="image">Image</option>
                          <option value="video">Video</option>
                          <option value="document">Document</option>
                        </select>
                      </div>

                      {data.headerType === 'text' && (
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                            Header Text
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500"
                              name="headerText"
                              value={data.headerText || ''}
                              onChange={handleChange}
                              placeholder="Header text..."
                              maxLength={60}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">
                              {(data.headerText || '').length}/60
                            </div>
                          </div>
                        </div>
                      )}

                      {(data.headerType === 'image' ||
                        data.headerType === 'video' ||
                        data.headerType === 'document') && (
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                            Header Media
                          </label>
                          <input
                            type="file"
                            className="hidden"
                            ref={fileInputRef}
                            onChange={handleMediaUpload}
                            accept={
                              data.headerType === 'image'
                                ? 'image/*'
                                : data.headerType === 'video'
                                  ? 'video/*'
                                  : '*'
                            }
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="w-full flex flex-col items-center justify-center p-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl hover:bg-white hover:border-blue-400 transition-all group"
                          >
                            {uploading ? (
                              <>
                                <Loader2 className="h-6 w-6 text-blue-500 animate-spin mb-2" />
                                <span className="text-xs font-bold text-gray-500">
                                  Uploading...
                                </span>
                              </>
                            ) : (
                              <>
                                <div className="p-2 bg-white rounded-full shadow-sm text-gray-400 group-hover:text-blue-500 transition-colors mb-2">
                                  <Upload size={20} />
                                </div>
                                <span className="text-xs font-bold text-gray-500">
                                  {data.headerMediaId
                                    ? 'Change File'
                                    : 'Select File'}
                                </span>
                                {data.headerMediaId && (
                                  <p className="text-[10px] text-blue-600 mt-1 font-medium">
                                    ID: {data.headerMediaId}
                                  </p>
                                )}
                              </>
                            )}
                          </button>
                          <p className="text-[10px] text-gray-400 mt-2">
                            {data.headerType === 'document' && 'Max size 100MB'}
                            {data.headerType === 'video' && 'Max size 16MB'}
                            {data.headerType === 'image' && 'Max size 5MB'}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                        Footer Text (Optional)
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500"
                          name="footer"
                          value={data.footer || ''}
                          onChange={handleChange}
                          placeholder="Optional footer text..."
                          maxLength={60}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">
                          {(data.footer || '').length}/60
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                          Buttons ({data.buttons?.length || 0}/3)
                        </label>
                        <button
                          onClick={handleAddButton}
                          disabled={(data.buttons?.length || 0) >= 3}
                          className="p-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-200 transition-colors shadow-sm"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(data.buttons || []).map(button => (
                          <div
                            key={button.id}
                            className="flex gap-2 animate-in fade-in slide-in-from-top-1 duration-200"
                          >
                            <div className="relative flex-1">
                              <input
                                type="text"
                                className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 pr-12"
                                value={button.label}
                                onChange={e =>
                                  handleButtonLabelChange(
                                    button.id,
                                    e.target.value
                                  )
                                }
                                maxLength={20}
                                placeholder="Button Label"
                              />
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">
                                {button.label.length}/20
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveButton(button.id)}
                              className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                            >
                              <Minus size={18} />
                            </button>
                          </div>
                        ))}
                        {(data.buttons || []).length === 0 && (
                          <div className="text-xs text-center py-4 text-gray-400 italic bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            No buttons added yet
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Save reply as variable */}
                    <div className="pt-4 border-t border-gray-100">
                      <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5 tracking-widest">
                        Save Selected Button As
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 font-mono text-sm font-bold">{'{{'}  </span>
                        <input
                          type="text"
                          className="w-full pl-9 pr-9 p-2.5 bg-blue-50 border border-blue-200 rounded-xl text-sm font-mono focus:ring-2 focus:ring-blue-500 text-blue-800 placeholder-blue-300"
                          name="responseVariable"
                          value={data.responseVariable || ''}
                          onChange={handleChange}
                          placeholder="userChoice"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 font-mono text-sm font-bold">{'}}'}</span>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1.5 leading-relaxed">
                        In downstream nodes use <code className="bg-gray-100 px-1 rounded">{'{{userChoice}}'}</code> for the button ID and <code className="bg-gray-100 px-1 rounded">{'{{userChoice_title}}'}</code> for the label. <code className="bg-gray-100 px-1 rounded">{'{{_lastReply}}'}</code> always holds the last reply.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      case 'delayNode':
        return (
          <div className="space-y-4 flex flex-col">
            <div className="flex-1">
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                Wait Duration
              </label>
              <input
                type="number"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                name="duration"
                value={data.duration || ''}
                onChange={handleChange}
              />
            </div>
            <div className="flex-1 mt-4">
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                Time Unit
              </label>
              <select
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                name="unit"
                value={data.unit || 'minutes'}
                onChange={handleChange}
              >
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
          </div>
        );
      case 'conditionNode':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                Logic Condition
              </label>
              <input
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                name="condition"
                value={data.condition || ''}
                onChange={handleChange}
                placeholder="e.g. user_choice == 'Sales'"
              />
            </div>
          </div>
        );
      case 'apiNode':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                Method
              </label>
              <select
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                name="method"
                value={data.method || 'GET'}
                onChange={handleChange}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                API URL
              </label>
              <input
                type="text"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                name="url"
                value={data.url || ''}
                onChange={handleChange}
                placeholder="https://api.example.com/webhook"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                Headers (JSON format)
              </label>
              <textarea
                className="w-full h-24 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 font-mono text-xs"
                name="headers"
                value={data.headers || ''}
                onChange={handleChange}
                placeholder='{ "Authorization": "Bearer token" }'
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                Response Variable Name (Optional)
              </label>
              <input
                type="text"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                name="responseVariableName"
                value={data.responseVariableName || ''}
                onChange={handleChange}
                placeholder="myApiData"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                You can use this downstream like {'{{'}myApiData.data.name{'}}'}
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Response Branches
                </label>
                <button
                  onClick={() => {
                    const cases = data.responseCases || [];
                    setData(prev => ({
                      ...prev,
                      responseCases: [
                        ...cases,
                        {
                          id: `case_${Date.now()}`,
                          variablePath: 'statusCode',
                          operator: '==',
                          value: '200',
                        },
                      ],
                    }));
                  }}
                  className="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors shadow-sm"
                >
                  <Plus size={14} />
                </button>
              </div>

              <div className="space-y-3">
                {(data.responseCases || []).map((c, index) => (
                  <div
                    key={c.id}
                    className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-2 relative animate-in fade-in slide-in-from-top-1 duration-200"
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[10px] font-bold text-gray-500 uppercase">
                        Branch {index + 1}
                      </span>
                      <button
                        onClick={() => {
                          setData(prev => ({
                            ...prev,
                            responseCases: prev.responseCases.filter(
                              rc => rc.id !== c.id
                            ),
                          }));
                        }}
                        className="text-red-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Path (e.g. statusCode, data.status)"
                        className="w-full p-2 text-xs border rounded-lg focus:ring-1 focus:ring-emerald-500"
                        value={c.variablePath}
                        onChange={e => {
                          const newCases = [...data.responseCases];
                          newCases[index].variablePath = e.target.value;
                          setData(prev => ({
                            ...prev,
                            responseCases: newCases,
                          }));
                        }}
                      />
                      <div className="flex gap-2">
                        <select
                          className="p-2 text-xs border rounded-lg focus:ring-1 focus:ring-emerald-500 bg-white"
                          value={c.operator}
                          onChange={e => {
                            const newCases = [...data.responseCases];
                            newCases[index].operator = e.target.value;
                            setData(prev => ({
                              ...prev,
                              responseCases: newCases,
                            }));
                          }}
                        >
                          <option value="==">==</option>
                          <option value="!=">!=</option>
                          <option value=">">&gt;</option>
                          <option value="<">&lt;</option>
                          <option value="contains">contains</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Value"
                          className="flex-1 p-2 text-xs border rounded-lg focus:ring-1 focus:ring-emerald-500"
                          value={c.value}
                          onChange={e => {
                            const newCases = [...data.responseCases];
                            newCases[index].value = e.target.value;
                            setData(prev => ({
                              ...prev,
                              responseCases: newCases,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {(data.responseCases || []).length === 0 && (
                  <div className="text-xs text-center py-4 text-gray-400 italic border-2 border-dashed border-gray-100 rounded-xl">
                    No conditional branches.
                    <br />
                    Flow will continue to default path.
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      default:
        return (
          <p className="text-gray-500 text-sm">
            No configuration available for this node type.
          </p>
        );
    }
  };

  const getIcon = () => {
    switch (selectedNode.type) {
      case 'triggerNode':
        return <Zap className="h-5 w-5 text-primary-500" />;
      case 'messageNode':
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'delayNode':
        return <Clock className="h-5 w-5 text-amber-500" />;
      case 'conditionNode':
        return <GitBranch className="h-5 w-5 text-indigo-500" />;
      case 'apiNode':
        return <Globe className="h-5 w-5 text-emerald-500" />;
      default:
        return null;
    }
  };

  return (
    <aside className="fixed right-0 top-16 bottom-0 w-80 bg-white border-l border-gray-200 shadow-2xl z-20 flex flex-col animate-in slide-in-from-right duration-300">
      <div className="p-6 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-50 rounded-lg border border-gray-100 italic">
            {getIcon()}
          </div>
          <h3 className="font-bold text-gray-900 capitalize italic">
            {selectedNode.type.replace('Node', '')} Settings
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">{renderConfig()}</div>

      <div className="p-6 border-t border-gray-100 bg-gray-50 flex flex-col gap-3">
        <button
          onClick={handleSave}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-200 active:scale-95"
        >
          <Save className="h-4 w-4" />
          Apply Changes
        </button>
        <button
          onClick={() => onDelete(selectedNode.id)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-red-600 font-bold border border-red-100 rounded-xl hover:bg-red-50 transition-all active:scale-95"
        >
          <Trash2 className="h-4 w-4" />
          Delete Node
        </button>
      </div>
    </aside>
  );
};

export default NodeConfigSidebar;
